import { useState } from "react";
import { ExternalLink, Clock, Filter, Lock, Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useNewsItems, type NewsItem } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import PricingDialog from "@/components/PricingDialog";

type Topic = "all" | "ukraine" | "gaza" | "middleeast" | "americas" | "asia" | "global";

const topicColors: Record<Exclude<Topic, "all">, string> = {
  ukraine: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  gaza: "bg-danger/20 text-danger border-danger/30",
  middleeast: "bg-warning/20 text-warning border-warning/30",
  americas: "bg-violet-500/20 text-violet-400 border-violet-500/30",
  asia: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  global: "bg-primary/20 text-primary border-primary/30",
};

const topicLabels: Record<Topic, string> = {
  all: "All",
  ukraine: "Ukraine",
  gaza: "Gaza",
  middleeast: "Middle East",
  americas: "Americas",
  asia: "Asia",
  global: "Global",
};

// Mock fallback
const mockNews: NewsItem[] = [
  {
    id: "1", headline: "UN Security Council emergency session called over escalation",
    excerpt: "The United Nations Security Council has called an emergency session to address the rapidly deteriorating situation in the region.",
    source_name: "Reuters", url: "#", published_at: new Date(Date.now() - 900000).toISOString(), topic: "global",
    provider: "mock", ingested_at: new Date().toISOString(), created_at: new Date().toISOString(),
  },
  {
    id: "2", headline: "Air defense systems intercept multiple drones over Odesa",
    excerpt: "Ukrainian air defense forces reported successfully intercepting several unmanned aerial vehicles approaching from the Black Sea.",
    source_name: "AP News", url: "#", published_at: new Date(Date.now() - 1800000).toISOString(), topic: "ukraine",
    provider: "mock", ingested_at: new Date().toISOString(), created_at: new Date().toISOString(),
  },
  {
    id: "3", headline: "Humanitarian corridor negotiations underway in Gaza",
    excerpt: "International mediators are working to establish safe humanitarian corridors for civilian evacuation and aid delivery.",
    source_name: "Al Jazeera", url: "#", published_at: new Date(Date.now() - 3600000).toISOString(), topic: "gaza",
    provider: "mock", ingested_at: new Date().toISOString(), created_at: new Date().toISOString(),
  },
  {
    id: "4", headline: "Satellite imagery reveals new military positions in Donetsk",
    excerpt: "Commercial satellite analysis shows significant changes in military deployments across the Donetsk front line.",
    source_name: "Reuters", url: "#", published_at: new Date(Date.now() - 5400000).toISOString(), topic: "ukraine",
    provider: "mock", ingested_at: new Date().toISOString(), created_at: new Date().toISOString(),
  },
  {
    id: "5", headline: "Regional tensions rise following Red Sea shipping incident",
    excerpt: "Commercial shipping disruptions continue in the Red Sea corridor amid escalating tensions in the region.",
    source_name: "AP News", url: "#", published_at: new Date(Date.now() - 7200000).toISOString(), topic: "middleeast",
    provider: "mock", ingested_at: new Date().toISOString(), created_at: new Date().toISOString(),
  },
];

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString();
};

const NewsFeed = () => {
  const [activeTopic, setActiveTopic] = useState<Topic>("all");
  const { isPremium, user } = useAuth();
  const navigate = useNavigate();
  const { data: dbNews, isLoading } = useNewsItems(activeTopic, isPremium);
  const [pricingOpen, setPricingOpen] = useState(false);
  const { formatPriceWithEur } = useLocalCurrency();

  const news = dbNews && dbNews.length > 0 ? dbNews : mockNews;
  const filtered = activeTopic === "all"
    ? news
    : news.filter((n) => n.topic === activeTopic);

  return (
    <>
    <section id="news" className="border-b border-border">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-foreground">Live Conflict News</h2>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-danger" />
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Breaking updates from verified sources</p>
          </div>
          {!isPremium && (
            <Badge variant="outline" className="w-fit border-warning/30 text-warning">
              <Filter className="mr-1 h-3 w-3" />
              Free: 5 articles • 1h delay
            </Badge>
          )}
        </div>

        {/* Topic filters */}
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {(Object.keys(topicLabels) as Topic[]).map((topic) => (
            <button
              key={topic}
              onClick={() => setActiveTopic(topic)}
              className={`shrink-0 rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
                activeTopic === topic
                  ? "border-primary bg-primary/20 text-primary"
                  : "border-border bg-secondary text-muted-foreground hover:border-primary/50"
              }`}
            >
              {topicLabels[topic]}
            </button>
          ))}
        </div>

        {/* News cards */}
        <div className="space-y-3">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))
          ) : (
            <AnimatePresence mode="popLayout">
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className="border-border bg-card transition-colors hover:border-primary/30">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase ${topicColors[item.topic as Exclude<Topic, "all">] || topicColors.global}`}>
                              {topicLabels[item.topic as Topic] || item.topic}
                            </span>
                            <span className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {formatTime(item.published_at)}
                            </span>
                          </div>
                          <h3 className="mb-1 text-sm font-semibold leading-tight text-foreground">
                            {item.headline}
                          </h3>
                          <p className="text-xs leading-relaxed text-muted-foreground line-clamp-2">
                            {item.excerpt}
                          </p>
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-[10px] font-medium text-muted-foreground">
                              {item.source_name}
                            </span>
                            {item.url && (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                              >
                                Read Full Article
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {/* Blurred locked cards for free users */}
          {!isPremium && (
            <div className="relative mt-3 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="pointer-events-none select-none blur-sm">
                  <Card className="border-border bg-card">
                    <CardContent className="p-4">
                      <div className="mb-2 h-4 w-24 rounded bg-muted" />
                      <div className="mb-1 h-4 w-3/4 rounded bg-muted" />
                      <div className="h-3 w-1/2 rounded bg-muted" />
                    </CardContent>
                  </Card>
                </div>
              ))}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Lock className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="mb-2 text-sm font-semibold text-foreground">
                  Unlock unlimited articles in real-time
                </p>
                <Button size="sm" className="gap-1.5 font-semibold" onClick={() => setPricingOpen(true)}>
                  <Zap className="h-3.5 w-3.5" />
                  {`See all events now — ${formatPriceWithEur(2.99)}/mo`}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Premium CTA (only for free) */}
        {!isPremium && (
          <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-4 text-center">
            <p className="mb-2 text-sm font-medium text-foreground">
              Unlock real-time updates, unlimited history & advanced filters
            </p>
            <Button size="sm" className="font-semibold" onClick={() => setPricingOpen(true)}>
              {`Upgrade to Premium — ${formatPriceWithEur(2.99)}/mo`}
            </Button>
          </div>
        )}
      </div>
    </section>
    <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </>
  );
};

export default NewsFeed;
